import * as Ariakit from '@ariakit/react'
import classNames from 'classnames'
import * as React from 'react'

import { usePrevious } from '../hooks/use-previous'
import { Input, InputComponentProps } from './input'
import { Tooltip, TooltipColor, TooltipProps, TooltipTrigger } from './tooltip'

interface TooltipSettingsProps
  extends Pick<TooltipProps, 'gutter' | 'isPointerEventsEnabled' | 'className'>,
    Pick<Ariakit.TooltipStoreProps, 'placement'> {
  color?: keyof typeof TooltipColor
}

interface TooltipInputProps extends InputComponentProps {
  /**
   * Settings for Tooltip component and useTooltipStore
   */
  tooltipSettings?: TooltipSettingsProps

  /**
   * Whether or not the tooltip shows when user hovers over the input
   */
  showTooltipOnHover?: boolean
}

function TooltipInputComponent(
  { tooltipSettings, name, showTooltipOnHover = false, children, ...props }: React.PropsWithChildren<TooltipInputProps>,
  ref: React.ForwardedRef<HTMLInputElement>
) {
  const { color, gutter, placement = 'bottom', className: tooltipClass, isPointerEventsEnabled } = tooltipSettings ?? {}
  const prevMessage = usePrevious(children, true)

  const [isFocused, setIsFocused] = React.useState(false)
  const handleFocus = React.useRef(() => setIsFocused(true))
  const handleBlur = React.useRef(() => setIsFocused(false))

  const input = <Input {...props} ref={ref} name={name} />
  if (!children && !prevMessage) return input

  return (
    <Ariakit.TooltipProvider
      showTimeout={0}
      placement={placement}
      open={showTooltipOnHover ? undefined : isFocused && !!children}
    >
      <TooltipTrigger
        state={undefined}
        hideOnBlur
        showOnHover={showTooltipOnHover}
        render={input}
        onFocus={handleFocus.current}
        onBlur={handleBlur.current}
      />
      <Tooltip
        state={undefined}
        isPointerEventsEnabled={isPointerEventsEnabled}
        id={name}
        color={color}
        gutter={gutter}
        className={classNames(tooltipClass, 'px-2.5')}
        hideOnHoverOutside={showTooltipOnHover}
        data-testid={name?.length ? `${name}-tooltip` : undefined}
      >
        {children ?? prevMessage}
      </Tooltip>
    </Ariakit.TooltipProvider>
  )
}

export const TooltipInput = React.forwardRef(TooltipInputComponent)
